import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '../supabase';

/** Zod boundary for `_internal_seats_over_limit`'s RPC response — see its
 * grant in `supabase/migrations/20260806121000_seat_write_gate.sql`. */
const SeatsOverLimitSchema = z.boolean();

/**
 * The shape of {@link SubscriptionStatus.limits}, mirroring `public.tiers.limits`.
 *
 * ## Corrected 2026-08-18 (vendor#153) — this declared two keys that do not exist
 *
 * It read `{ writes_allowed, ebay_sync_allowed, max_active_users }`, and the
 * last two are not in the database. Live `public.tiers.limits` is
 * `{"max_seats": …, "writes_allowed": true}` on both rows, measured. **eBay sync
 * is a dropped feature** (`CLAUDE.md`, "What was dropped vs. Storefront"), and
 * `max_active_users` is storefront's name for what this repo calls `max_seats`.
 *
 * Both were typed NON-OPTIONAL and read `undefined` at runtime; `strictNullChecks`
 * cannot catch that, because the value arrives over the wire. No production code
 * read either — only test fixtures, which is why nothing had failed yet.
 *
 * Its docblock carried three further claims, all false and all inherited verbatim
 * from `docs/features/epic-15--tier-gate-framework.md`, a **storefront** document
 * the fork carried over:
 *
 *   1. *"Every key is required server-side via the `tiers_limits_shape_check`
 *      CHECK constraint."* The live constraint is
 *      `COALESCE(jsonb_typeof(limits->'writes_allowed'), '') = 'boolean'` — it
 *      requires exactly ONE key, and says nothing about the others.
 *   2. It cited migration `20260519100000_tier_limits_shape`. **There is no such
 *      migration in this repo.**
 *   3. It named `_internal_can_write_for_shop` as "the enforcement". That is a
 *      storefront function; there is no `shop_id` here at all. The vendor
 *      equivalent is `_internal_can_write_for_account`, and what it actually
 *      gates is five money tables — see `CLAUDE.md`'s "Tier limits" bullet and
 *      vendor#123.
 *
 * **Do not "fix" this by adding the two keys to `tiers.limits`.** That would
 * invent tier axes nobody agreed to and resurrect a dropped feature.
 */
export interface TierLimits {
  writes_allowed: boolean;
  /** `null` on `vendor_pro` historically meant "no tier ceiling, Stripe governs";
   *  the effective cap is `least(accounts.seat_limit, this)`. */
  max_seats: number | null;
}

export type SubscriptionStatusValue = 'trialing' | 'active' | 'past_due' | 'canceled';

export interface SubscriptionStatus {
  tierId: string;
  status: SubscriptionStatusValue;
  limits: TierLimits;
  /**
   * Convenience: the subscription is live AND tier writes are allowed.
   * Live = 'active', or 'trialing' with the 7-day trial window
   * (current_period_end) still in the future — mirrors the server-side
   * `_internal_can_write_for_account` gate (vendor#153 — this said
   * `_internal_can_write_for_shop`, a storefront function that does not exist
   * here). Note that gate covers the five money tables and not the whole write
   * surface — see vendor#123 before relying on it.
   */
  writesAllowed: boolean;
  /** While trialing: the trial deadline; once subscribed: the billing period end. */
  currentPeriodEnd: Date | null;
  /** True when status is 'trialing' but the 7-day window has passed (or was never set). */
  trialExpired: boolean;
  /** Whole days left in the trial (ceil, min 0); null when not trialing. */
  trialDaysLeft: number | null;
  /**
   * True iff the subscription itself is active/trialing-live, independent of
   * seat count.
   *
   * **It does NOT mirror `_internal_subscription_is_live` (corrected
   * 2026-08-17, vendor#796 item 3).** That claim stood here through the #789
   * fix round, which edited this same docblock a few lines below without
   * touching it. Measured against the live function: the server ANDs the
   * tier's own `writes_allowed` into its result, so it answers "may this
   * account write" — the client field that corresponds to it is
   * {@link SubscriptionStatus.writesAllowed}, which ANDs the same flag plus the
   * seat cap. This field is the narrower question the server has no single
   * predicate for: is the SUBSCRIPTION live, whatever the tier and the seats
   * say. Naming the wrong server function is how a caller reaches for the wrong
   * field.
   * Distinguishes a seats-over-limit account (`subscriptionLive: true,
   * writesAllowed: false`) from a genuinely lapsed one (`subscriptionLive:
   * false`) — the two need different `/subscribe` copy and a different
   * remedy (vendor#619).
   *
   * **Required, not optional (corrected 2026-08-11, PR #789 fix round).**
   * This field used to be `subscriptionLive?: boolean`, marked optional "so
   * every existing mock/test literal typed as `SubscriptionStatus` keeps
   * compiling unchanged." That reasoning runs backwards — it weakens the
   * production type to spare tests, which is exactly the shape `router.tsx`
   * then had to pay for with a `sub?.subscriptionLive ?? false` fallback
   * that silently reads "not live" for a value the hook actually always
   * sets (code review, PR #789). The real hook has always set both fields;
   * the test fixtures that omitted them were updated instead (see
   * `makeSubscriptionStatus()` in `./makeSubscriptionStatus.ts`).
   */
  subscriptionLive: boolean;
  /**
   * True when the account holds more active seats than its plan allows —
   * mirrors the server's `_internal_seats_over_limit()`. The remedy is
   * removing a member, not paying (vendor#53's write-gate design: an
   * over-limit account goes read-only rather than auto-disabling a member).
   * Required for the same reason as {@link SubscriptionStatus.subscriptionLive}.
   */
  seatsOverLimit: boolean;
}

interface ShopWithTierRow {
  tier_id: string;
  subscription_status: SubscriptionStatusValue;
  current_period_end: string | null;
  tiers: { limits: TierLimits } | null;
}

/**
 * Source of truth for "may this shop write right now?" in the UI.
 *
 * Shell: any. The data is shop-scoped, not shell-scoped — kiosk routes never
 * call this (they're anonymous and have no writes to gate), but the hook is
 * safe to import from any shell.
 *
 * Contracts:
 *   - RLS: the SELECT runs under the caller's JWT and the `shops_member_select`
 *     policy filters out non-member shops. Non-members see `data === null`.
 *   - Cache: keyed on accountId; staleTime 30s matches the global QueryClient
 *     default. Stripe-webhook-driven status changes (deferred work) will
 *     invalidate this key when they ship.
 *   - The query DOES NOT block on a missing accountId — it returns `enabled: false`
 *     so callers can render a "no shop selected" branch instead of a loading
 *     spinner that never resolves.
 *   - **`writesAllowed` also folds in the seats arm (vendor#619).** It is
 *     `tiers.limits.writes_allowed && subscriptionLive && !seatsOverLimit` —
 *     the same three-part predicate the server's `_internal_can_write_for_
 *     account` enforces (`_internal_subscription_is_live() AND NOT
 *     _internal_seats_over_limit()`). Before vendor#619 this hook never
 *     consulted seats at all, so an account that Stripe downgraded below its
 *     member count read as `writesAllowed: true` here — server writes were
 *     already refused (where the write gate is wired — see CLAUDE.md's
 *     "Tier limits" section for how far that reaches today), but the UI never
 *     reached an explanatory screen. `subscriptionLive` and `seatsOverLimit`
 *     are also returned individually so a caller (`/subscribe`) can tell an
 *     over-seat account (`subscriptionLive: true`) apart from a genuinely
 *     lapsed one (`subscriptionLive: false`) — the two need different copy
 *     and a different remedy (remove a member vs. pay).
 *   - `_internal_seats_over_limit(p_account_id)` is `GRANT EXECUTE TO
 *     authenticated` (`supabase/migrations/20260806121000_seat_write_gate.sql`)
 *     — directly callable here, no Edge Function needed. The account row and
 *     the seats RPC are independent reads (neither needs the other's
 *     result), so they run via `Promise.all` rather than two serial
 *     `await`s — one round trip instead of two on the path that gates first
 *     paint (code review, PR #789 Medium 1).
 *   - **A seats-RPC failure degrades to `seatsOverLimit: false` rather than
 *     throwing the whole query (fixed 2026-08-11, PR #789 Medium 1).** The
 *     RPC previously threw on any error, which failed this ENTIRE query and
 *     locked every user out of the app (`RequireSubscription` treats a
 *     failed/undefined `sub` as `writesAllowed: false`) on a transient
 *     failure of one narrow signal. `_internal_seats_over_limit`'s own name
 *     marks it `_internal_` — not a promise of availability — and this
 *     hook's own `_internal_`-prefixed dependency is otherwise unpinned: no
 *     pgTAP assertion pins `authenticated`'s EXECUTE grant on it, so a
 *     backend-side tightening of `_internal_` helpers could silently break
 *     this call with nothing here to catch it — filed for the backend as
 *     vendor#795 (PR #789 S3). Treating
 *     an error here as "not over" restores exactly the pre-vendor#619
 *     behavior on failure — this is a deliberate CLIENT-side fail-open; the
 *     real enforcement is the server's write-gate RLS
 *     (`_internal_can_write_for_account`), which does not consult this hook
 *     at all. That gate does not yet cover the inventory write surface this
 *     bulk reprice action touches (CLAUDE.md "Tier limits", #123) — so
 *     failing open here does not, on its own, let a write through that the
 *     server would otherwise have refused.
 */
export function useSubscriptionStatus(accountId: string | null) {
  return useQuery({
    queryKey: ['subscription-status', accountId],
    enabled: Boolean(accountId),
    queryFn: async (): Promise<SubscriptionStatus | null> => {
      if (!accountId) return null;
      const [{ data, error }, seatsResult] = await Promise.all([
        supabase
          .from('accounts')
          .select('tier_id, subscription_status, current_period_end, tiers(limits)')
          .eq('id', accountId)
          .maybeSingle<ShopWithTierRow>(),
        supabase.rpc('_internal_seats_over_limit', { p_account_id: accountId }),
      ]);
      if (error) throw error;
      if (!data?.tiers) return null;
      const periodEnd = data.current_period_end ? new Date(data.current_period_end) : null;
      const trialLive =
        data.subscription_status === 'trialing' &&
        periodEnd !== null &&
        periodEnd.getTime() > Date.now();
      const subscriptionLive = data.subscription_status === 'active' || trialLive;
      const trialExpired = data.subscription_status === 'trialing' && !trialLive;
      // vendor#796 item 2: `.parse()` here reproduced the lockout that
      // security-reviewer's Medium 1 removed from the RPC-failure path — a
      // malformed (non-error) payload threw, the whole hook errored, and the
      // account read as unable to write. Unreachable today (the underlying
      // count aggregate is never NULL), which is exactly why the throwing
      // version survived review; `safeParse` fails the same way the `error`
      // branch above already does — open, not shut.
      const seatsParsed = SeatsOverLimitSchema.safeParse(seatsResult.data);
      const seatsOverLimit = seatsResult.error ? false : seatsParsed.success && seatsParsed.data;
      return {
        tierId: data.tier_id,
        status: data.subscription_status,
        limits: data.tiers.limits,
        writesAllowed: data.tiers.limits.writes_allowed && subscriptionLive && !seatsOverLimit,
        currentPeriodEnd: periodEnd,
        trialExpired,
        trialDaysLeft:
          data.subscription_status === 'trialing'
            ? Math.max(0, Math.ceil(((periodEnd?.getTime() ?? 0) - Date.now()) / 86_400_000))
            : null,
        subscriptionLive,
        seatsOverLimit,
      };
    },
  });
}
